import { Box, Paper, TableCell, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import { useStoredDate } from "../hooks/useStoredDate";
import { RankedUser } from "../models/RankedUser";
import { CURRENT_YEAR_MAX_DATE, DatePickerComponent, DatePickerWrapper, MIN_DATE } from "../components/AppToolbar";
import ReportTable from "../components/ReportTable";
import AccomplishmentService from "../services/AccomplishmentService";
import { RankingCategory } from "../utils/enums/RankingCategory";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import GenericTabComponent from "../components/GenericTabComponent";
import StringUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/StringUtils";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { useHistory, useLocation } from "react-router-dom";
import { Token } from "../theme";

export const MonthlyRanking = () => {
  const history = useHistory();
  const [reportHeaders, setReportHeaders] = useState<ReportHeader[]>([]);
  const [rows, setRows] = useState<RankedUser[]>([]);
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const searchParams = new URLSearchParams(useLocation().search);
  const category = searchParams.get("category");
  const month = searchParams.get("month");
  const isInitialMount = useRef(true);

  const { updateUrl } = useUpdateUrl(Routes.RANKING, searchParams);

  useEffect(() => {
    if (category !== null) {
      setSelectedTab(parseInt(category));
    }

    if (month !== null) {
      const newDate = new Date();
      newDate.setMonth(parseInt(month) - 1);
      setSelectedDate(newDate);
    }
    updateSearchParams();
    loadHeaders();
    getRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // We do this to prevent double fetching
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      updateSearchParams();
      loadHeaders();
      getRanking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedTab]);

  const loadHeaders = () => {
    setReportHeaders(new Array<ReportHeader>({ label: "Member", align: "left" }, { label: "Points", align: "left" }));
  };

  const updateSearchParams = () => {
    searchParams.set("category", selectedTab.toString());
    searchParams.set("month", (selectedDate.getMonth() + 1).toString());
    updateUrl();
  };

  const getRanking = async () => {
    setRows(
      (
        await AccomplishmentService.getMonthlyRanking(
          selectedDate.getMonth() + 1,
          selectedDate.getFullYear(),
          Object.values(RankingCategory)[selectedTab],
        )
      ).getContentOrThrowError(),
    );
  };

  const changeTab = async (_event: React.SyntheticEvent, tab: number) => {
    setSelectedTab(tab);
  };

  const getHeader = () => {
    const tabArray = Object.values(RankingCategory).map((tab) => {
      return StringUtils.snakeCaseToSentence(tab);
    });

    //TODO: Improve this section
    return (
      <>
        <Box>
          <Paper elevation={0} style={{ paddingBottom: "5px", marginTop: ".5rem" }}>
            <div style={{ display: "flex", marginBottom: "0rem", alignItems: "flex-start", marginTop: "0px" }}>
              <div>
                <Typography variant="h6" style={{ padding: "0 0 1em 0", color: Token.textPrimary }}>
                  {"Monthly Points Ranking"}
                </Typography>
                <DatePickerWrapper
                  style={{
                    display: "flex",
                    borderRadius: "4px",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "1rem",
                    border: `${Token.borderDefault} solid 1px`,
                    boxSizing: "inherit",
                  }}
                >
                  <DatePickerComponent
                    views="month"
                    label="Month"
                    value={selectedDate}
                    inputFormat="MMMM"
                    onChange={(selectedDate) => {
                      setSelectedDate(selectedDate!);
                    }}
                  />
                  <DatePickerComponent
                    views="year"
                    label="Year"
                    value={selectedDate}
                    inputFormat="yyyy"
                    minDate={MIN_DATE}
                    maxDate={CURRENT_YEAR_MAX_DATE}
                    onChange={(selectedDate) => {
                      setSelectedDate(selectedDate!);
                    }}
                  />
                </DatePickerWrapper>
              </div>
            </div>
          </Paper>
        </Box>
        <GenericTabComponent selectedTab={selectedTab} handleTabChange={changeTab} tabs={tabArray} />
      </>
    );
  };

  const getBody = () => {
    return (
      <>
        <ReportTable<RankedUser>
          headers={reportHeaders}
          rows={rows}
          tableRow={(instance) => (
            <>
              <TableCell>
                {instance.firstName} {instance.lastName}
              </TableCell>
              <TableCell>{instance.score}</TableCell>
            </>
          )}
        />
      </>
    );
  };

  return (
    <>
      <AppContainerFullHeight topSection={getHeader()} bottomSection={getBody()} />
    </>
  );
};
